import type Quill from 'quill/core';
import { useCallback, useEffect } from 'react';
import type { HTMLAttributes, MouseEvent } from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';
import Header from './formats/Header';
import { getHeaderCollapse } from './useHeaderCollapse';
import findClosestScrollingContainer from './utils/findClosestScrollingContainer';
import { isOrganizationOrigin } from './utils/matchCurrentUrl';
import parsePostPath from './utils/parsePostPath';
import safeParseUrl from '../utils/url/safeParseUrl';

// Not using native `Element.scrollIntoView` because it doesn't support
// setting `boundary`. We need it because we don't want to scroll the main
// editor when clicking inside embed post content.
const scrollIntoViewWithinBoundary = (node: HTMLElement) => {
  const boundary = findClosestScrollingContainer(node);
  scrollIntoView(node, { boundary, block: 'start' });
};

const safeQuerySelector = (element: Element, selector: string) => {
  try {
    return element.querySelector(selector);
  } catch (err) {
    return null;
  }
};

const scrollHeaderNodeIntoView = (quill: Quill, headerNode: HTMLElement) => {
  // Scroll to the header directly when it's visible.
  if (headerNode.offsetParent) {
    scrollIntoViewWithinBoundary(headerNode);
    return;
  }

  // Otherwise, scroll to the nearest visible parent header.
  const allHeaders: { domNode: HTMLElement }[] =
    getHeaderCollapse(quill)?.getCollapsedHeaders() ?? [];

  const visibleHeaders = allHeaders.filter(
    ({ domNode }) => domNode.offsetParent,
  );

  const nearestVisibleHeader = visibleHeaders.toReversed().filter(
    (visibleHeader) =>
      headerNode.compareDocumentPosition(visibleHeader.domNode) & // eslint-disable-line no-bitwise
      Node.DOCUMENT_POSITION_PRECEDING,
  )[0];

  if (nearestVisibleHeader?.domNode) {
    scrollIntoViewWithinBoundary(nearestVisibleHeader.domNode);
  }
};

const getHeaderNodeByAnchor = (quill: Quill, hash: string) => {
  let decoded: string | undefined;
  try {
    decoded = decodeURIComponent(hash);
  } catch (error) {}

  if (!decoded) return null;

  const header = safeQuerySelector(
    quill.root,
    Header.getDOMSelector(decoded.slice(1)),
  );

  return header instanceof HTMLElement ? header : null;
};

const EVENTS_FOR_INTERACTION_DETECTION: (keyof WindowEventMap)[] = [
  'wheel',
  'pointerdown',
  'keydown',
];

/**
 * Scrolls to the anchor when the page loads.
 * This hook detects position changes caused by asynchronously loaded elements
 * and re-scrolls to the anchor until user interaction occurs.
 */
export const useScrollingToAnchorOnLoad = (quill: Quill | null) =>
  useEffect(() => {
    const { hash } = document.location;
    const headerNode = quill && hash && getHeaderNodeByAnchor(quill, hash);
    if (!headerNode) return undefined;

    const observer = new ResizeObserver(() =>
      scrollHeaderNodeIntoView(quill, headerNode),
    );
    observer.observe(quill.root);

    const cleanup = () => {
      observer.disconnect();
      EVENTS_FOR_INTERACTION_DETECTION.forEach((event) =>
        window.removeEventListener(event, cleanup, true),
      );
    };

    EVENTS_FOR_INTERACTION_DETECTION.forEach((event) =>
      window.addEventListener(event, cleanup, true),
    );
    return cleanup;
  }, [quill]);

// We handle the click event in capture phase to prevent the default behavior
// of React Router's `Link` component.
// At the same time, we still want to allow descendant element handlers to
// take priority. This map is used to store the last task.
const LAST_SCROLLING_TASKS = new WeakMap<
  MouseEvent,
  {
    hash: string;
    options: {
      quill: Quill;
      headerNode: HTMLElement;
      updateLocationHash: boolean;
    };
  }
>();

const useAnchorScrollingHandler = (
  quill: Quill | null,
  postId: string,
  { updateLocationHash = false } = {},
): Pick<HTMLAttributes<HTMLElement>, 'onClickCapture'> => {
  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (!quill) return;

      const taskFromAncestors = LAST_SCROLLING_TASKS.get(e);
      if (taskFromAncestors) {
        const headerNode = getHeaderNodeByAnchor(quill, taskFromAncestors.hash);
        if (headerNode) {
          LAST_SCROLLING_TASKS.set(e, {
            ...taskFromAncestors,
            options: {
              quill,
              headerNode,
              updateLocationHash,
            },
          });
        }
        return;
      }

      // Modifier keys won't trigger in-page scroll. For example, on macOS:
      // * metaKey opens the link in a new tab.
      // * shiftKey opens the link in a new window.
      // * ctrlKey triggers a context menu.
      // * altKey downloads the link.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const closestAnchor =
        e.target instanceof Element && e.target.closest('a');
      if (!closestAnchor || closestAnchor.isContentEditable) {
        return;
      }

      const url = safeParseUrl(closestAnchor.href);
      if (
        !url?.hash ||
        !isOrganizationOrigin(url) ||
        parsePostPath(url.pathname)?.id !== postId
      ) {
        return;
      }
      const headerNode = getHeaderNodeByAnchor(quill, url.hash);
      if (!headerNode) return;
      e.preventDefault();

      LAST_SCROLLING_TASKS.set(e, {
        hash: url.hash,
        options: {
          quill,
          headerNode,
          updateLocationHash,
        },
      });

      // Allow descendant element handlers to override
      queueMicrotask(() => {
        // Scroll after `Quill#scrollSelectionIntoView()` is called
        setTimeout(() => {
          const task = LAST_SCROLLING_TASKS.get(e);
          if (task) {
            if (task.options.updateLocationHash) {
              window.location.hash = task.hash;
            }
            scrollHeaderNodeIntoView(
              task.options.quill,
              task.options.headerNode,
            );
          }
        }, 0);
      });
    },
    [quill, postId, updateLocationHash],
  );

  return { onClickCapture: handleClick };
};

export default useAnchorScrollingHandler;
